import { capitalize, resizeVtexImage, sortSizes } from "@core/utils"
import { metaAdapter } from "@meta/index"
import { normalizeBrand } from "@src/core/farm"
import type { InitialConfig, ItemsResponse } from "@src/types"
import type {
  NVProduct,
  NVItemsContext,
  NVBrandContext,
  NVLineItemsContext,
  NVValueContext,
  NVAvailableGridContext,
} from "./types"
import {
  getActiveItem,
  getDefaultSeller,
  getItemImage,
  getVariantColor,
  getVariantSize,
  getItemCategory,
  getItemCategory2,
} from "./utils"

export const nvAdapter = {
  ...metaAdapter, // global adapters

  items: (context: NVItemsContext): ItemsResponse[] | null => {
    if (context?.items) return context.items
    if (!context?.products) return null

    return context.products.map((product: NVProduct) => {
      const activeItem = getActiveItem(product)
      const seller = getDefaultSeller(activeItem)
      const offer = seller?.commertialOffer

      const listPrice = offer?.ListPrice ?? product.priceRange?.listPrice?.lowPrice
      const price = offer?.Price ?? product.priceRange?.sellingPrice?.lowPrice ?? null

      return {
        quantity: 1,
        index: product?.index ?? 0,
        item_brand: capitalize(product.brand) ?? null,
        item_name: product.productName ?? null,
        price,
        discount: Math.round((Number(listPrice) - Number(price)) * 100) / 100,
        item_variant: getVariantColor(activeItem), // cor
        item_variant2: getVariantSize(activeItem), // tamanho
        item_shipping_tier: "",
        item_ref: product.productReference ?? null,
        item_category: getItemCategory(product),
        item_category2: getItemCategory2(product) || null,
        item_id: product.productId ?? null,
        item_sku: activeItem?.itemId ?? null,
        item_url: product.link ?? (product.linkText ? `/${product.linkText}/p` : null),
        image_url: resizeVtexImage(getItemImage(activeItem)) ?? null,
        seller_id: seller?.sellerId ?? null,
        item_list_name: product?.item_list_name ?? null,
      }
    })
  },

  brand: (context: NVBrandContext, config: InitialConfig) => {
    if (config?.brand) return config.brand
    if (context?.brand) return context.brand
    if (context?.products) {
      return normalizeBrand(context.products.map((p) => p?.brand))
    }
    return null
  },

  line_items: (context: NVLineItemsContext): string | null => {
    if (context?.line_items) return context.line_items
    if (!context?.products) return null

    return context.products
      .map((p: NVProduct) => p?.productId)
      .filter((id): id is string => Boolean(id))
      .join()
  },

  value: (context: NVValueContext): number | null => {
    if (context?.value) return context.value
    if (!context?.products) return null

    return context.products.map((p: NVProduct) => getDefaultSeller(getActiveItem(p))?.commertialOffer?.Price ?? p.priceRange?.sellingPrice?.lowPrice)
      .filter((price): price is number => typeof price === "number")
      .reduce((sum, price) => sum + price, 0)
  },

  available_grid: (context: NVAvailableGridContext): string | null => {
    if (context?.available_grid) return context.available_grid
    if (!context?.products?.length) return null

    const sizes = context.products.flatMap((product: NVProduct) =>
      (product.items ?? [])
        .filter((item) =>
          item.sellers?.some((s) => (s.commertialOffer?.AvailableQuantity ?? 0) > 0)
        )
        .map((item) => item.name?.split(" - ")[1]?.toUpperCase())
        .filter((size): size is string => Boolean(size))
    )

    return sortSizes(sizes).join(",").trim() || null
  },
}
